import { useMutation, useQuery } from "@apollo/client";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { toDateOnlyInTimezone } from "@/lib/dateHelpers";
import { useNavigate } from "react-router-dom";
import { useRef, useEffect, useState, useMemo } from "react";
import { JOB_POST_DETAILS_QUERY } from "@/pages/sections/[sectionId]/jobs/job-post/[jobPostId]/queries";
import JobPostForm from "@/components/Forms/JobPostForm";
import { JobPostFormInput } from "@/components/Forms/JobPostForm/types";
import {
  formatJobPostForForm,
  formatFormDataForMutation,
  isAdvancedOrExecutiveWithQuestions
} from "@/components/Forms/JobPostForm/utils";
import { UPDATE_JOB_POST_MUTATION } from "@/components/Forms/JobPostForm/queries";
import {
  AssistedJobPostDraftSchema,
  AssistedJobPostPublishSchema,
  SelfServeJobPostDraftSchema,
  SelfServeJobPostSchema
} from "@/components/Forms/JobPostForm/schema";
import { AdvancedJobPostPublishSchema, AdvancedJobPostDraftSchema } from "./advancedSchema";
import { ApplicationQuestion, Option } from "@/pages/_layouts/jobs/types";

interface EditJobPostFormProps {
  jobPostId: string;
  sectionId?: string;
  onSuccess?: (jobPostId: string) => void;
}

const SUBMISSION_DEADLINE_ZONE = "America/New_York";

export default function EditJobPostForm({ jobPostId, sectionId, onSuccess }: EditJobPostFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSecondarySubmitting, setIsSecondarySubmitting] = useState(false);
  const [triggerConfetti, setTriggerConfetti] = useState(false);
  const CONFETTI_MIN_DURATION = 2200;
  const confettiTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const formRef = useRef<{ getValues: () => JobPostFormInput } | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [errorFacilityId, setErrorFacilityId] = useState<string | null>(null);

  const {
    loading: jobPostLoading,
    data,
    error
  } = useQuery(JOB_POST_DETAILS_QUERY, {
    variables: { jobPostId },
    skip: !jobPostId,
    fetchPolicy: "network-only"
  });

  const segments = window.location.pathname.split("/");
  const basePath = segments.slice(0, -2).join("/");
  const backUrl = `${basePath}/${jobPostId}/details`;

  useEffect(() => {
    if (error) {
      navigate(backUrl);
      setErrorBanner("Error fetching job post");
    }
  }, [error, navigate, backUrl]);

  const [patchJobPost] = useMutation(UPDATE_JOB_POST_MUTATION);

  const originalDefaultValues = useMemo(() => {
    if (!data?.jobPost) return null;
    const formatted = formatJobPostForForm(data.jobPost);
    if (sectionId) {
      (formatted as any).sectionId = sectionId;
    }
    return formatted;
  }, [data?.jobPost, sectionId]);

  const isFormDataUnchanged = (formData: JobPostFormInput, original: JobPostFormInput | null): boolean => {
    if (!original) return false;

    // Normalize values for comparison
    const normalize = (val: any): any => {
      if (val === null || val === undefined || val === "") return "";
      if (typeof val === "string") return val.trim();
      return val;
    };

    const normalizeDeadlineDate = (date: string | undefined) =>
      normalize(toDateOnlyInTimezone(date, SUBMISSION_DEADLINE_ZONE) ?? date);

    // Compare key fields that matter for validation
    const fieldsToCompare: (keyof JobPostFormInput)[] = [
      "name",
      "overview",
      "facilityId",
      "serviceLevel",
      "category",
      "positionType",
      "employerId",
      "pointOfContact",
      "compensation",
      "requiredEducation",
      "requiredExperienceInYears",
      "requiredExperienceDescription",
      "essentialResponsibilities",
      "applicantGroup",
      "applyVia",
      "paySchedule",
      "submissionDeadline",
      "externalJobUrl",
      "benefits",
      "additionalIncome",
      "additionalPgaEducation",
      "otherSections",
      "otherStates",
      "hideCompensationFromJobBoard",
      "owners"
    ];

    for (const field of fieldsToCompare) {
      const current = normalize(formData[field]);
      const originalValue = normalize(original[field]);

      // Handle nested objects (pointOfContact, compensation, submissionDeadline)
      if (field === "pointOfContact") {
        const currentPoc = formData.pointOfContact;
        const originalPoc = original.pointOfContact;
        if (
          normalize(currentPoc?.name) !== normalize(originalPoc?.name) ||
          normalize(currentPoc?.email) !== normalize(originalPoc?.email) ||
          normalize(currentPoc?.title) !== normalize(originalPoc?.title) ||
          normalize(currentPoc?.phone) !== normalize(originalPoc?.phone)
        ) {
          return false;
        }
        continue;
      }

      if (field === "compensation") {
        const currentComp = formData.compensation;
        const originalComp = original.compensation;

        // Convert original compensation from dollars to cents for comparison (form stores in cents)
        const originalBaseMaximum = originalComp?.baseMaximum
          ? typeof originalComp.baseMaximum === "number"
            ? originalComp.baseMaximum * 100
            : originalComp.baseMaximum
          : undefined;
        const originalBaseMinimum = originalComp?.baseMinimum
          ? typeof originalComp.baseMinimum === "number"
            ? originalComp.baseMinimum * 100
            : originalComp.baseMinimum
          : undefined;
        const originalTotalAnticipated = originalComp?.totalAnticipated
          ? typeof originalComp.totalAnticipated === "number"
            ? originalComp.totalAnticipated * 100
            : originalComp.totalAnticipated
          : undefined;

        if (
          normalize(currentComp?.baseMaximum) !== normalize(originalBaseMaximum) ||
          normalize(currentComp?.baseMinimum) !== normalize(originalBaseMinimum) ||
          normalize(currentComp?.totalAnticipated) !== normalize(originalTotalAnticipated) ||
          normalize(currentComp?.currencyUnit) !== normalize(originalComp?.currencyUnit)
        ) {
          return false;
        }
        continue;
      }

      if (field === "submissionDeadline") {
        const currentDeadline = formData.submissionDeadline;
        const originalDeadline = original.submissionDeadline;

        // Normalize original deadline to object format if it's a string/Date
        let normalizedOriginal: { startDate?: string; endDate?: string } | undefined;
        if (typeof originalDeadline === "string" || originalDeadline instanceof Date) {
          const normalizedDate = toDateOnlyInTimezone(originalDeadline, SUBMISSION_DEADLINE_ZONE);
          normalizedOriginal = { startDate: normalizedDate, endDate: normalizedDate };
        } else if (originalDeadline && typeof originalDeadline === "object") {
          normalizedOriginal = {
            startDate:
              toDateOnlyInTimezone(originalDeadline.startDate, SUBMISSION_DEADLINE_ZONE) ?? originalDeadline.startDate,
            endDate:
              toDateOnlyInTimezone(originalDeadline.endDate, SUBMISSION_DEADLINE_ZONE) ?? originalDeadline.endDate
          };
        }

        // Compare normalized values
        const currentStart = normalizeDeadlineDate(currentDeadline?.startDate);
        const currentEnd = normalizeDeadlineDate(currentDeadline?.endDate);
        const originalStart = normalizeDeadlineDate(normalizedOriginal?.startDate);
        const originalEnd = normalizeDeadlineDate(normalizedOriginal?.endDate);

        if (currentStart !== originalStart || currentEnd !== originalEnd) {
          return false;
        }
        continue;
      }

      if (field === "owners") {
        const currentOwnerIds = (formData.owners || []).map((owner) => owner.id).sort();
        const originalOwnerIds = (original.owners || []).map((owner) => owner.id).sort();

        if (JSON.stringify(currentOwnerIds) !== JSON.stringify(originalOwnerIds)) {
          return false;
        }
        continue;
      }

      // Handle arrays
      if (Array.isArray(current) && Array.isArray(originalValue)) {
        if (current.length !== originalValue.length) {
          return false;
        }
        const currentSorted = [...current].sort().map(normalize);
        const originalSorted = [...originalValue].sort().map(normalize);
        if (JSON.stringify(currentSorted) !== JSON.stringify(originalSorted)) {
          return false;
        }
        continue;
      }

      // Simple comparison for other fields
      if (current !== originalValue) {
        return false;
      }
    }

    return true;
  };

  async function handleSubmit(formData: JobPostFormInput, isSecondaryAction?: boolean) {
    try {
      if (isSecondaryAction) {
        setIsSecondarySubmitting(true);
      } else {
        setIsSubmitting(true);
      }

      let schema;
      if (formData.serviceLevel === "Self-Serve") {
        schema = isSecondaryAction ? SelfServeJobPostDraftSchema : SelfServeJobPostSchema;
      } else if (formData.serviceLevel === "Assisted") {
        schema = isSecondaryAction ? AssistedJobPostDraftSchema : AssistedJobPostPublishSchema;
      } else {
        schema = isSecondaryAction ? AdvancedJobPostDraftSchema : AdvancedJobPostPublishSchema;
      }

      const isUsingDraftSchema =
        isSecondaryAction === true ||
        schema === AssistedJobPostDraftSchema ||
        schema === AdvancedJobPostDraftSchema ||
        schema === SelfServeJobPostDraftSchema;

      const isUnchanged = isFormDataUnchanged(formData, originalDefaultValues as JobPostFormInput | null);
      const shouldIgnoreServiceLevelErrors =
        formData.serviceLevel === "Self-Serve" && isUsingDraftSchema && isUnchanged;

      const validationResult = await schema.safeParseAsync(formData);

      if (!validationResult.success) {
        let filteredErrors = validationResult.error.issues;
        if (shouldIgnoreServiceLevelErrors) {
          filteredErrors = validationResult.error.issues.filter(
            (issue) => !issue.path || issue.path.length === 0 || issue.path[0] !== "serviceLevel"
          );
        }

        // If there are still errors after filtering, show them
        if (filteredErrors.length > 0) {
          setErrorBanner("Please fix form errors before submitting");
          console.error(filteredErrors);
          return;
        }
        // If only serviceLevel errors were present and we're ignoring them, continue
      }

      setErrorBanner(null);

      const inputData = formatFormDataForMutation(formData, jobPostId);
      const applicationData = {
        id: formData.application?.id,
        questions: formData.application?.questions.map((question: ApplicationQuestion, index: number) => ({
          ...(!question.id?.includes("new-question") && { id: question.id }),
          content: question.content,
          questionType: question.questionType,
          position: index + 1,
          options: question.options.map((option: Option, optionIndex: number) => ({
            id: option.id,
            content: option.content,
            position: optionIndex + 1
          }))
        }))
      };

      try {
        const response = await patchJobPost({
          variables: {
            input: inputData,
            ...(isAdvancedOrExecutiveWithQuestions(formData) ? { application: applicationData } : {})
          }
        });

        if (response.data.patchJobPost.success) {
          if (isSecondaryAction) {
            if (onSuccess) {
              onSuccess(jobPostId);
            } else {
              navigate(backUrl);
            }
          } else {
            setTriggerConfetti(true);
            confettiTimeoutRef.current = setTimeout(() => {
              handleConfettiComplete();
            }, CONFETTI_MIN_DURATION);
          }
          const successMessage = isSecondaryAction
            ? "Draft saved"
            : inputData.sendForReview === true
              ? "Job post sent for review"
              : "Job post updated";
          showSnackbar(successMessage);
        } else {
          const errorMessage = response.data.patchJobPost.message || "Error updating job post";

          if (errorMessage === "Employer does not have permissions at assigned facility." && formData.facilityId) {
            setErrorBanner("Employer does not have Manage Job role at assigned facility.");
            setErrorFacilityId(formData.facilityId);
          } else {
            setErrorBanner(errorMessage);
            setErrorFacilityId(null);
          }
          showErrorSnackbar(errorMessage);
        }
      } catch (error: any) {
        setErrorBanner(error.message || "Error updating job post");
      }
    } catch (error: any) {
      setErrorBanner(error.message || "Error validating form data");
    } finally {
      setIsSecondarySubmitting(false);
      setIsSubmitting(false);
    }
  }

  const handleConfettiComplete = () => {
    if (confettiTimeoutRef.current) {
      clearTimeout(confettiTimeoutRef.current);
      confettiTimeoutRef.current = null;
    }

    setTriggerConfetti(false);

    setTimeout(() => {
      if (onSuccess) {
        onSuccess(jobPostId);
      } else {
        navigate(backUrl);
      }
    }, 0);
  };

  if (jobPostLoading && !triggerConfetti) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg font-medium">Loading job post data...</p>
        </div>
      </div>
    );
  }

  if ((!data?.jobPost || !originalDefaultValues) && !triggerConfetti) {
    return <div>Job post not found</div>;
  }

  return (
    <JobPostForm
      isEdit={true}
      defaultValues={originalDefaultValues!}
      onSubmit={handleSubmit}
      formRef={formRef}
      isSubmitting={isSubmitting}
      isSecondarySubmitting={isSecondarySubmitting}
      triggerConfetti={triggerConfetti}
      onConfettiComplete={handleConfettiComplete}
      errorBanner={errorBanner || undefined}
      setErrorBanner={(errorBanner) => setErrorBanner(errorBanner || null)}
      errorFacilityId={errorFacilityId}
    />
  );
}
